// frontend/src/pages/cargo/CertificateStep.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from './../../services/api';

const CANCEL_ALLOWED_ROLES = ['underwriter', 'admin'];

export default function CertificateStep({
    acceptance,
    onComplete,
    onProceedToSchedule,
    onGoToVoyage,
}) {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const canCancel = CANCEL_ALLOWED_ROLES.includes(user?.role);

    const [certData, setCertData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelled, setCancelled] = useState(false);

    // PDF preview
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    // Missing fields popup
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [fieldValues, setFieldValues] = useState({});
    const [savingFields, setSavingFields] = useState(false);
    const [fieldsError, setFieldsError] = useState('');
    const [lastPayloadSent, setLastPayloadSent] = useState(null);

    // Mode mismatch (Air vs Sea)
    const [modeMismatch, setModeMismatch] = useState(null);
    const [showModeMismatchModal, setShowModeMismatchModal] = useState(false);

    const hasGenerated = useRef(false);
    const pdfObjectUrlRef = useRef(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!hasGenerated.current && acceptance?.id) {
            hasGenerated.current = true;
            generateCertificate();
        }
        // eslint-disable-next-line
    }, [acceptance?.id]);

    useEffect(() => {
        return () => {
            if (pdfObjectUrlRef.current) {
                window.URL.revokeObjectURL(pdfObjectUrlRef.current);
                pdfObjectUrlRef.current = null;
            }
        };
    }, []);

    // ============================================================
    // GENERATE CERTIFICATE
    // ============================================================
    const generateCertificate = async (extraFields = null) => {
        if (!acceptance?.id) {
            setError('No acceptance record found. Please go back and complete the previous steps.');
            return;
        }

        setLoading(true);
        setError('');
        setModeMismatch(null);
        setShowModeMismatchModal(false);

        try {
            // 1. Persist any extra fields the user just typed in the popup
            if (extraFields && Object.keys(extraFields).length > 0) {
                await api.post('/cargo/certificate/fields', {
                    acceptance_id: acceptance.id,
                    fields: extraFields,
                });
            }

            // 2. Ask the backend to build the payload and issue the certificate
            const { data } = await api.post('/cargo/certificate', {
                acceptance_id: acceptance.id,
            });

            const cert = data.body || data;
            if (!isMountedRef.current) return;
            setCertData(cert);
            setCancelled(!!cert.cancelled_at);
            setShowFieldsModal(false);
            setMissingFields([]);
            setFieldValues({});
            setLastPayloadSent(null);

            if (cert?.already_exists) {
                console.info('[CertificateStep] Existing active certificate:', cert.certificateNo);
            }

            if (cert?.id) loadPdfPreview(cert.id);
            if (onComplete) onComplete(cert);
        } catch (err) {
            console.error('[CertificateStep] Error:', err);
            if (!isMountedRef.current) return;
            const respData = err.response?.data || {};
            const status = err.response?.status;

            // ---------------------------------------------------------
            // Mode mismatch (e.g. sea port on an Air shipment)
            // ---------------------------------------------------------
            if (status === 422 && respData.code === 'MODE_MISMATCH') {
                const detail = (respData.missing_fields || [])[0] || {};
                setModeMismatch({
                    message: respData.error || 'Selected port does not match the shipment mode.',
                    field: respData.field || detail.key,
                    received: detail.received,
                    receivedCode: detail.received_code,
                    expectedMode: detail.expected_mode,
                    expectedKind: detail.expected_kind,
                    currentKind: detail.current_kind,
                });
                setShowModeMismatchModal(true);
                setError(respData.error || 'Selected port does not match the shipment mode.');
                return;
            }

            // ---------------------------------------------------------
            // Missing fields — show the popup
            // ---------------------------------------------------------
            if (status === 422 && (respData.code === 'MISSING_FIELDS' || Array.isArray(respData.missing_fields))) {
                const fields = respData.missing_fields || [];
                setMissingFields(fields);

                // Build initial values from:
                //   a) whatever came back as `current_values` (already resolved from cover + IDF)
                //   b) any value the user already typed in a prior attempt
                //   c) empty string as the fallback
                const initial = {};
                const currentValues = respData.current_values || {};
                for (const f of fields) {
                    initial[f.key] =
                        currentValues[f.key] ||
                        fieldValues[f.key] ||
                        '';
                }

                // Try to enrich with the cover values as a last resort
                try {
                    const { data: fieldInfo } = await api.get(
                        `/cargo/certificate/fields/${acceptance.id}`
                    );
                    if (fieldInfo?.current_values) {
                        for (const f of fields) {
                            if (!initial[f.key] && fieldInfo.current_values[f.key]) {
                                initial[f.key] = fieldInfo.current_values[f.key];
                            }
                        }
                    }
                } catch (fetchErr) {
                    console.warn('[CertificateStep] Could not enrich fields:', fetchErr.message);
                }

                setFieldValues(initial);
                setFieldsError('');
                setShowFieldsModal(true);
                setError('');
                setLastPayloadSent(respData.dmvic_payload || null);
                return;
            }

            // ---------------------------------------------------------
            // Any other error
            // ---------------------------------------------------------
            setError(
                respData.error ||
                respData.details ||
                respData.message ||
                'Failed to generate certificate. Please try again.'
            );
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    };

    // ============================================================
    // PDF PREVIEW
    // ============================================================
    const loadPdfPreview = useCallback(async (certId, attempt = 1) => {
        if (!certId) return;
        const MAX_ATTEMPTS = 4;
        const BACKOFFS = [0, 1500, 3000, 5000];

        if (isMountedRef.current) {
            setPdfLoading(true);
            if (attempt === 1) setPdfError('');
        }

        try {
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5038/api/v1';
            const url = `${apiBase}/cargo/certificates/${certId}/download`;

            const res = await fetch(url, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!res.ok) {
                let errMsg = `Preview failed (${res.status})`;
                let isRetryable =
                    res.status === 404 ||
                    res.status === 502 ||
                    res.status === 503 ||
                    res.status === 202;
                try {
                    const errJson = await res.json();
                    errMsg = errJson.error || errMsg;
                    if (errJson.still_processing) isRetryable = true;
                    if (/processing|not available|still|being generated/i.test(errMsg)) {
                        isRetryable = true;
                    }
                } catch {
                    /* ignore */
                }

                if (isRetryable && attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error(errMsg);
            }

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('pdf')) {
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error(`Server did not return a PDF (got ${contentType})`);
            }

            const blob = await res.blob();
            const arrayBuffer = await blob.slice(0, 4).arrayBuffer();
            const magic = new TextDecoder().decode(new Uint8Array(arrayBuffer));
            if (magic !== '%PDF') {
                if (attempt < MAX_ATTEMPTS) {
                    await new Promise((r) => setTimeout(r, BACKOFFS[attempt] || 5000));
                    if (!isMountedRef.current) return;
                    return loadPdfPreview(certId, attempt + 1);
                }
                throw new Error('Preview file is not a valid PDF');
            }

            if (pdfObjectUrlRef.current) {
                window.URL.revokeObjectURL(pdfObjectUrlRef.current);
            }
            const objectUrl = window.URL.createObjectURL(blob);
            pdfObjectUrlRef.current = objectUrl;
            if (isMountedRef.current) {
                setPdfUrl(objectUrl);
                setPdfError('');
            }
        } catch (err) {
            console.error('[CertificateStep] PDF preview error:', err);
            if (isMountedRef.current) {
                setPdfError(err.message || 'Could not load preview. Try the Reload Preview button.');
            }
        } finally {
            if (isMountedRef.current) setPdfLoading(false);
        }
    }, []);

    const openPdfInNewTab = () => {
        if (!pdfUrl) return;
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    };

    const handleFieldChange = (key, value) => {
        setFieldValues((prev) => ({ ...prev, [key]: value }));
    };

    // ============================================================
    // SUBMIT POPUP FIELDS + RE-ISSUE
    // We save first, then re-run generateCertificate WITHOUT the
    // extra fields (they're now persisted).
    // ============================================================
    const handleSubmitFields = async () => {
        const missing = [];
        for (const f of missingFields) {
            if (!fieldValues[f.key] || String(fieldValues[f.key]).trim() === '') {
                missing.push(f.label || f.key);
            }
        }
        if (missing.length) {
            setFieldsError(`Please fill: ${missing.join(', ')}`);
            return;
        }

        setSavingFields(true);
        setFieldsError('');

        try {
            // Persist the overrides
            await api.post('/cargo/certificate/fields', {
                acceptance_id: acceptance.id,
                fields: fieldValues,
            });

            // Close the popup and retry
            setShowFieldsModal(false);
            setMissingFields([]);
            setFieldValues({});
            hasGenerated.current = false;
            await generateCertificate();
        } catch (err) {
            console.error('[CertificateStep] Save fields failed:', err);
            const respData = err.response?.data || {};

            if (err.response?.status === 422 && respData.code === 'MODE_MISMATCH') {
                setShowFieldsModal(false);
                const detail = (respData.missing_fields || [])[0] || {};
                setModeMismatch({
                    message: respData.error || 'Selected port does not match the shipment mode.',
                    field: respData.field || detail.key,
                    received: detail.received,
                    receivedCode: detail.received_code,
                    expectedMode: detail.expected_mode,
                    expectedKind: detail.expected_kind,
                    currentKind: detail.current_kind,
                });
                setShowModeMismatchModal(true);
                return;
            }

            setFieldsError(respData.error || 'Could not save fields. Please try again.');
        } finally {
            setSavingFields(false);
        }
    };

    const handleCancelFields = () => {
        setShowFieldsModal(false);
        setFieldsError('');
        setError('Certificate generation cancelled. You can try again when ready.');
    };

    const handleGoToVoyage = () => {
        setShowModeMismatchModal(false);
        setModeMismatch(null);
        if (onGoToVoyage) onGoToVoyage();
    };

    // ============================================================
    // DOWNLOAD / VERIFY / CANCEL
    // ============================================================
    const downloadCertificate = async () => {
        if (!certData) return;
        setDownloading(true);

        try {
            const certId = certData.id || acceptance.id;
            const token = localStorage.getItem('token');
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5038/api/v1';
            const url = `${apiBase}/cargo/certificates/${certId}/download`;

            const res = await fetch(url, {
                method: 'GET',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!res.ok) {
                let errMsg = `Download failed (${res.status})`;
                try {
                    const errJson = await res.json();
                    errMsg = errJson.error || errMsg;
                } catch {
                    /* ignore */
                }
                throw new Error(errMsg);
            }

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('pdf')) {
                throw new Error('Server did not return a PDF.');
            }

            const blob = await res.blob();
            const arrayBuffer = await blob.slice(0, 4).arrayBuffer();
            const magic = new TextDecoder().decode(new Uint8Array(arrayBuffer));
            if (magic !== '%PDF') throw new Error('Downloaded file is not a valid PDF');

            const objectUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            const suffix = cancelled ? '-CANCELLED' : '';
            a.download = `${certData.certificateNo || 'cargo-certificate'}${suffix}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(objectUrl);

            setDownloaded(true);
        } catch (err) {
            console.error('[CertificateStep] Download error:', err);
            alert(err.message || 'Could not download certificate.');
        } finally {
            setDownloading(false);
        }
    };

    const verifyCertificate = async () => {
        if (!certData?.certificateNo) {
            setVerifyResult({ error: 'No certificate number available to verify' });
            return;
        }

        setVerifying(true);
        setVerifyResult(null);

        try {
            const { data } = await api.post('/cargo/certificates/verify', {
                certificate_number: certData.certificateNo,
            });
            setVerifyResult(data);
        } catch (err) {
            setVerifyResult({ error: err.response?.data?.error || 'Verification failed' });
        } finally {
            setVerifying(false);
        }
    };

    const cancelCertificate = async () => {
        if (!certData?.certificateNo) return;
        if (!canCancel) {
            alert('You do not have permission to cancel certificates. Please contact an underwriter or administrator.');
            return;
        }

        const confirmText = `Are you sure you want to cancel certificate ${certData.certificateNo}?\n\nThe certificate will be marked as cancelled on DMVIC but the PDF will remain downloadable for your records.`;

        if (!window.confirm(confirmText)) return;

        setCancelling(true);
        try {
            const { data } = await api.post('/cargo/certificates/cancel', {
                certificate_number: certData.certificateNo,
                cancel_reason: 1,
            });

            const updatedCert = data.certificate || {
                ...certData,
                cancelled_at: new Date().toISOString(),
                cancelled_by_name: user?.full_name || 'You',
                status: 'cancelled',
            };

            setCertData((prev) => ({ ...prev, ...updatedCert }));
            setCancelled(true);

            if (updatedCert.id || certData.id) {
                await loadPdfPreview(updatedCert.id || certData.id);
            }

            alert('Certificate cancelled successfully. The PDF remains available for download.');
        } catch (err) {
            console.error('[CertificateStep] Cancel error:', err);
            alert(
                err.response?.data?.error ||
                err.response?.data?.details ||
                'Failed to cancel certificate.'
            );
        } finally {
            setCancelling(false);
        }
    };

    const handleContinueToSchedule = () => {
        if (onComplete) onComplete(certData);
        if (onProceedToSchedule) onProceedToSchedule();
    };

    const formatDate = (val) => {
        if (!val) return '-';
        try {
            let iso = String(val);
            if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)) {
                iso = iso.replace(' ', 'T') + 'Z';
            }
            return new Date(iso).toLocaleString('en-KE', {
                timeZone: 'Africa/Nairobi',
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        } catch {
            return val;
        }
    };

    const formatMoney = (val) => {
        const n = Number(val || 0);
        if (!isFinite(n)) return '$0';
        return `$${n.toLocaleString()}`;
    };

    // ==================== LOADING ====================
    if (loading && !certData && !showModeMismatchModal) {
        return (
            <div className="max-w-2xl mx-auto text-center py-20 animate-fadeIn">
                <div className="relative inline-flex items-center justify-center w-24 h-24 mb-6">
                    <div className="absolute inset-0 rounded-full bg-[#3a663f]/20 animate-ping" />
                    <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#2d5232] to-[#3a663f] flex items-center justify-center shadow-2xl">
                        <svg className="w-10 h-10 text-white animate-spin" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </div>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 dark:text-white mb-2">Generating Certificate</h3>
                <p className="text-gray-500 dark:text-slate-400">Please wait while we process your request with DMVIC...</p>
            </div>
        );
    }

    // ==================== ERROR (no cert) ====================
    if (error && !certData && !showModeMismatchModal && !showFieldsModal) {
        return (
            <div className="max-w-2xl mx-auto text-center py-16 animate-fadeIn">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-3xl p-8 border border-red-100 dark:border-red-900/30 shadow-xl">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
                        <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-bold text-red-800 dark:text-red-400 mb-2">Certificate Generation Failed</h3>
                    <p className="text-red-600 dark:text-red-300 mb-6">{error}</p>
                    <button
                        onClick={() => {
                            hasGenerated.current = false;
                            generateCertificate();
                        }}
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors shadow-lg"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // ==================== MAIN ====================
    return (
        <div className="max-w-5xl mx-auto animate-fadeIn space-y-6">
            {/* TOP ERROR BANNER (only when cert exists or modal is open) */}
            {error && (certData || showFieldsModal || showModeMismatchModal) && (
                <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-5 py-4 rounded-2xl flex items-start gap-3 shadow-md">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1 text-sm font-medium">{error}</div>
                    <button
                        onClick={() => setError('')}
                        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {/* HERO HEADER */}
            <div className="text-center space-y-4">
                <div className="relative inline-flex items-center justify-center w-24 h-24">
                    <div className={`absolute inset-0 rounded-full ${cancelled ? 'bg-red-500/20' : 'bg-[#3a663f]/20'} animate-ping`} />
                    <div className={`relative w-20 h-20 rounded-full bg-gradient-to-br ${cancelled ? 'from-red-500 to-red-600' : 'from-[#2d5232] to-[#3a663f]'} flex items-center justify-center shadow-2xl`}>
                        {cancelled ? (
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>
                </div>

                <h2 className="text-4xl font-extrabold text-gray-800 dark:text-white tracking-tight">
                    {cancelled ? 'Certificate Cancelled' : 'Certificate Issued Successfully'}
                </h2>

                <p className="text-gray-500 dark:text-slate-400 max-w-md mx-auto">
                    {cancelled ? (
                        'This certificate has been cancelled with DMVIC but remains downloadable for your records.'
                    ) : (
                        <>
                            Your marine cargo insurance with{' '}
                            <strong className="text-[#3a663f] dark:text-green-400">Mayfair Insurance</strong> is now active.
                        </>
                    )}
                </p>
            </div>

            {/* STATUS PILLS */}
            <div className="flex flex-wrap items-center justify-center gap-3">
                <div
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${cancelled
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
                        }`}
                >
                    {cancelled ? (
                        <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Cancelled
                        </>
                    ) : (
                        <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                            Active
                        </>
                    )}
                </div>
                {certData?.certificateNo && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-full text-xs font-mono font-semibold border border-gray-200 dark:border-slate-600">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {certData.certificateNo}
                    </div>
                )}
            </div>

            {/* CERT DETAILS CARD */}
            {certData && (
                <div
                    className={`bg-white dark:bg-slate-800/90 backdrop-blur-sm rounded-3xl shadow-2xl border-2 ${cancelled
                            ? 'border-red-200 dark:border-red-800'
                            : 'border-gray-100 dark:border-slate-700'
                        } overflow-hidden`}
                >
                    <div
                        className={`p-5 text-white relative overflow-hidden bg-gradient-to-r ${cancelled
                                ? 'from-red-600 via-red-500 to-red-600'
                                : 'from-[#2d5232] via-[#3a663f] to-[#4d8c54]'
                            }`}
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
                                        IRA Marine Cargo Certificate
                                    </p>
                                    <p className="text-2xl font-extrabold tracking-tight">
                                        {certData.certificateNo || 'Processing...'}
                                    </p>
                                </div>
                            </div>
                            <div className="hidden sm:block text-right">
                                <p className="text-xs text-white/70 uppercase tracking-wider">Insured Value</p>
                                <p className="text-2xl font-extrabold">{formatMoney(certData.insuredAmount)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        {cancelled && (
                            <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-2xl p-4 flex items-start gap-3">
                                <svg className="w-6 h-6 flex-shrink-0 mt-0.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <div>
                                    <p className="font-bold text-red-800 dark:text-red-300">This certificate has been cancelled.</p>
                                    <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
                                        It can no longer be used for customs clearance, but the PDF remains available for your records.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <InfoTile label="IDF Number" value={certData.idf_number} icon="🆔" />
                            <InfoTile label="Quote Number" value={certData.quote_number} icon="📋" />
                            <InfoTile label="IRA Policy" value={certData.iraPolicyNum || '—'} icon="📜" />
                            <InfoTile label="Transaction Ref" value={certData.transactionNo || '—'} icon="🔗" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100 dark:border-slate-700">
                            <DateTile
                                label="Issued On (EAT)"
                                value={certData.certificateDate}
                                accent="green"
                                icon={
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                }
                                formatDate={formatDate}
                            />
                            {cancelled && (
                                <DateTile
                                    label="Cancelled On (EAT)"
                                    value={certData.cancelled_at}
                                    accent="red"
                                    icon={
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                    }
                                    formatDate={formatDate}
                                />
                            )}
                        </div>

                        {/* AUDIT TRAIL */}
                        <div className="border-t border-gray-100 dark:border-slate-600 pt-6">
                            <div className="flex items-center gap-2 mb-4">
                                <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                                <h4 className="text-sm font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">
                                    Audit Trail
                                </h4>
                            </div>

                            <div className="space-y-3">
                                <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/30">
                                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-green-500/20">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="text-sm font-bold text-green-800 dark:text-green-300">Issued</p>
                                            <span className="text-xs text-green-600 dark:text-green-400">
                                                {formatDate(certData.certificateDate || certData.created_at)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                                            By{' '}
                                            <strong className="text-gray-800 dark:text-white">
                                                {certData.issued_by_name || 'System'}
                                            </strong>
                                        </p>
                                        {certData.transactionNo && (
                                            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1 font-mono">
                                                Ref: {certData.transactionNo}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {cancelled && (
                                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50/60 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30">
                                        <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-red-500/20">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="text-sm font-bold text-red-800 dark:text-red-300">Cancelled</p>
                                                <span className="text-xs text-red-600 dark:text-red-400">
                                                    {formatDate(certData.cancelled_at)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                                                By{' '}
                                                <strong className="text-gray-800 dark:text-white">
                                                    {certData.cancelled_by_name || 'System'}
                                                </strong>
                                            </p>
                                            {certData.cancel_reason && (
                                                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1 italic">
                                                    Reason: <span className="font-medium">#{certData.cancel_reason}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* PDF PREVIEW */}
                        <div className="border-t border-gray-100 dark:border-slate-600 pt-6">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-[#3a663f] dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    <h4 className="text-sm font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider">
                                        Certificate Preview
                                    </h4>
                                </div>
                                <div className="flex gap-2">
                                    {pdfUrl && (
                                        <button
                                            onClick={openPdfInNewTab}
                                            className="text-xs text-[#3a663f] dark:text-green-400 hover:underline font-semibold flex items-center gap-1"
                                        >
                                            Open in new tab
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </button>
                                    )}
                                    {certData?.id && (
                                        <button
                                            onClick={() => loadPdfPreview(certData.id)}
                                            className="text-xs text-[#3a663f] dark:text-green-400 hover:underline font-semibold"
                                        >
                                            Reload Preview
                                        </button>
                                    )}
                                </div>
                            </div>

                            {pdfLoading && (
                                <div className="rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50 flex flex-col items-center justify-center gap-3 py-16">
                                    <svg className="animate-spin h-10 w-10 text-[#3a663f] dark:text-green-400" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <p className="text-sm text-gray-500 dark:text-slate-400">Loading preview...</p>
                                </div>
                            )}

                            {pdfError && !pdfLoading && (
                                <div className="rounded-2xl border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 flex flex-col items-center justify-center gap-3 p-8 text-center">
                                    <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <p className="text-sm text-red-700 dark:text-red-300">{pdfError}</p>
                                    <button
                                        onClick={() => loadPdfPreview(certData.id)}
                                        className="text-xs text-red-600 dark:text-red-400 underline font-medium"
                                    >
                                        Try again
                                    </button>
                                </div>
                            )}

                            {pdfUrl && !pdfLoading && !pdfError && (
                                <div className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 shadow-inner">
                                    <iframe
                                        src={`${pdfUrl}#toolbar=1&navpanes=0`}
                                        title="Certificate PDF"
                                        className="w-full h-[500px] border-0"
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ACTIONS */}
                    <div className="px-6 py-5 bg-gray-50 dark:bg-slate-900/50 border-t border-gray-100 dark:border-slate-700">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={downloadCertificate}
                                disabled={downloading}
                                className={`flex-1 ${cancelled
                                        ? 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
                                        : 'bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54]'
                                    } text-white font-bold py-3.5 px-5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                            >
                                {downloading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Downloading...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        {downloaded
                                            ? 'Download Again'
                                            : cancelled
                                                ? 'Download Cancelled PDF'
                                                : 'Download Certificate'}
                                    </>
                                )}
                            </button>

                            <button
                                onClick={verifyCertificate}
                                disabled={verifying}
                                className="flex-1 bg-white dark:bg-slate-800 border-2 border-[#3a663f] dark:border-green-400 text-[#3a663f] dark:text-green-400 hover:bg-[#3a663f] hover:text-white font-bold py-3.5 px-5 rounded-2xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {verifying ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Verifying...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Verify Certificate
                                    </>
                                )}
                            </button>
                        </div>

                        {canCancel && !cancelled && certData.certificateNo && (
                            <div className="mt-3">
                                <button
                                    onClick={cancelCertificate}
                                    disabled={cancelling}
                                    className="w-full bg-white dark:bg-slate-800 border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-bold py-3.5 px-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
                                >
                                    {cancelling ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Cancelling...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Cancel Certificate
                                        </>
                                    )}
                                </button>
                                <p className="text-xs text-gray-400 dark:text-slate-500 text-center mt-2">
                                    Only use this if the certificate was issued incorrectly. The PDF will remain downloadable.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* VERIFY RESULT */}
            {verifyResult && (
                <div
                    className={`p-5 rounded-2xl flex items-start gap-3 shadow-lg ${verifyResult.error
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-2 border-red-200 dark:border-red-800'
                            : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-2 border-green-200 dark:border-green-800'
                        }`}
                >
                    <svg className="w-6 h-6 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {verifyResult.error ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        )}
                    </svg>
                    <div className="min-w-0">
                        <p className="font-bold">
                            {verifyResult.error ? 'Verification Failed' : 'Verification Successful'}
                        </p>
                        <p className="text-sm mt-0.5 break-words">
                            {verifyResult.error ||
                                verifyResult.message ||
                                verifyResult.statusMessage ||
                                'Certificate is authentic and valid.'}
                        </p>
                    </div>
                </div>
            )}

            {/* PRIMARY CTAs */}
            {certData && !cancelled && onProceedToSchedule && (
                <div className="text-center pt-2 space-y-3">
                    <button
                        onClick={handleContinueToSchedule}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Continue to Policy Schedule
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                    <p className="text-xs text-gray-400 dark:text-slate-500">
                        Generate and download your full marine policy schedule
                    </p>
                </div>
            )}

            {certData && (
                <div className="text-center pt-2">
                    <button
                        onClick={() => navigate('/cargo/certificates')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 border-2 border-[#3a663f] dark:border-green-400 text-[#3a663f] dark:text-green-400 hover:bg-[#3a663f] hover:text-white font-bold rounded-2xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        View All My Certificates
                    </button>
                </div>
            )}

            <div className="text-center pt-4">
                <p className="text-sm text-gray-400 dark:text-slate-500">
                    Need help?{' '}
                    <a
                        href="mailto:it@mayfair.co.ke"
                        className="text-[#3a663f] dark:text-green-400 font-semibold hover:underline"
                    >
                        Contact Mayfair Insurance Support
                    </a>
                </p>
            </div>

            {/* MISSING FIELDS MODAL */}
            {showFieldsModal && (
                <MissingFieldsModal
                    fields={missingFields}
                    values={fieldValues}
                    onChange={handleFieldChange}
                    onSubmit={handleSubmitFields}
                    onCancel={handleCancelFields}
                    saving={savingFields}
                    error={fieldsError}
                    lastPayloadSent={lastPayloadSent}
                />
            )}

            {/* MODE MISMATCH MODAL */}
            {showModeMismatchModal && modeMismatch && (
                <ModeMismatchModal
                    data={modeMismatch}
                    onClose={() => { setShowModeMismatchModal(false); setModeMismatch(null); }}
                    onGoToVoyage={onGoToVoyage ? handleGoToVoyage : null}
                />
            )}
        </div>
    );
}

// ==================== SUB-COMPONENTS ====================

function InfoTile({ label, value, icon }) {
    return (
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border border-gray-100 dark:border-slate-700">
            <div className="flex items-center gap-1.5 mb-1">
                <span className="text-sm">{icon}</span>
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500">
                    {label}
                </p>
            </div>
            <p className="text-sm font-bold text-gray-800 dark:text-white truncate" title={value || '—'}>
                {value || '—'}
            </p>
        </div>
    );
}

function DateTile({ label, value, accent, icon, formatDate }) {
    const colors =
        accent === 'red'
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400';

    return (
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${colors}`}>
            <div className="flex-shrink-0">{icon}</div>
            <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</p>
                <p className="text-sm font-bold truncate">
                    {value ? (formatDate ? formatDate(value) : new Date(value).toLocaleString()) : '—'}
                </p>
            </div>
        </div>
    );
}

function MissingFieldsModal({ fields, values, onChange, onSubmit, onCancel, saving, error, lastPayloadSent }) {
    // Show a compact preview of what the backend tried to send
    const showPayloadPreview = Boolean(lastPayloadSent && Object.keys(lastPayloadSent).length);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-5 text-white">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold">Additional Information Required</h3>
                            <p className="text-sm text-amber-100 mt-1">
                                DMVIC needs a few more details to issue your certificate.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                    {fields.map((f) => (
                        <div key={f.key}>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                                {f.label} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type={f.type || 'text'}
                                value={values[f.key] || ''}
                                onChange={(e) => onChange(f.key, e.target.value)}
                                placeholder={f.placeholder || ''}
                                className="w-full px-4 py-2.5 border-2 border-gray-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#3a663f] focus:border-transparent outline-none transition-all"
                            />
                            {f.error && (
                                <p className="text-xs text-red-600 mt-1">{f.error}</p>
                            )}
                            {f.dmvicMessage && (
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{f.dmvicMessage}</p>
                            )}
                        </div>
                    ))}

                    {showPayloadPreview && (
                        <details className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700">
                            <summary className="cursor-pointer text-xs font-semibold text-gray-600 dark:text-slate-400 px-3 py-2">
                                What was sent to DMVIC (for support)
                            </summary>
                            <pre className="text-[10px] leading-tight px-3 py-2 overflow-x-auto text-gray-600 dark:text-slate-400">
                                {JSON.stringify(lastPayloadSent, null, 2)}
                            </pre>
                        </details>
                    )}

                    {error && (
                        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
                            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {error}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900/50 flex flex-col sm:flex-row gap-3 justify-end border-t border-gray-100 dark:border-slate-700">
                    <button
                        onClick={onCancel}
                        disabled={saving}
                        className="px-5 py-2.5 bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={saving}
                        className="px-5 py-2.5 bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save &amp; Retry
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// MODE MISMATCH MODAL
// ============================================================
function ModeMismatchModal({ data, onClose, onGoToVoyage }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
                <div className="bg-gradient-to-r from-red-600 to-red-500 p-5 text-white">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold">Invalid Port for Transport Mode</h3>
                            <p className="text-sm text-red-100 mt-1">
                                The port on your cover doesn't match the shipment mode.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-700 dark:text-slate-300">{data.message}</p>

                    {data.received && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 space-y-2 text-xs">
                            <div className="flex justify-between items-center gap-3">
                                <span className="text-gray-500 dark:text-slate-400">Selected port</span>
                                <span className="font-mono text-red-700 dark:text-red-400 font-semibold text-right break-all">
                                    {data.received}{data.receivedCode ? ` (${data.receivedCode})` : ''}
                                </span>
                            </div>
                            <div className="flex justify-between items-center gap-3">
                                <span className="text-gray-500 dark:text-slate-400">Shipment mode</span>
                                <span className="font-bold text-red-700 dark:text-red-400">
                                    {data.expectedMode}
                                </span>
                            </div>
                            <div className="flex justify-between items-center gap-3">
                                <span className="text-gray-500 dark:text-slate-400">Required</span>
                                <span className="font-bold text-red-700 dark:text-red-400">
                                    a valid {data.expectedKind}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-start gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                            Go back to the Voyage step, select a valid {data.expectedKind}, save the cover,
                            then return here and retry the certificate.
                        </p>
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900/50 flex flex-col sm:flex-row gap-3 justify-end border-t border-gray-100 dark:border-slate-700">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 bg-white dark:bg-slate-800 border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        Close
                    </button>
                    {onGoToVoyage && (
                        <button
                            onClick={onGoToVoyage}
                            className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                        >
                            Change Port
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}